import { Button, Pagination } from '~/ui';
import { t } from 'i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { instance } from '~/utils/api/api';
import { useToastError } from '~/utils/useToastError';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { useEffect, useRef, useState } from 'react';
import styles from './ImageArtickeContainer.module.scss';
import PhotoPicker from '~/components/AnnouncementForm/PhotoPickerContainer/PhotoPicker/PhotoPicker';

interface IImage {
  id: number;
  full_photo_url: string;
  path: string;
}

interface IArticlesProps {
  multiselect?: boolean;
  assetId: number;
  selectedImage: IImage;
  onCloseModal: () => void;
  onSelectImages: (values: IImage) => void;
  onSelectImagesArray?: (values: IImage[] | []) => void;
}

const ImageArtickeContainer = ({
  selectedImage,
  assetId,
  onCloseModal,
  onSelectImages,
}: IArticlesProps) => {
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<IImage>(selectedImage);
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<IImage[]>([]);

  useEffect(() => {
    if (isUploading) {
      setUploadProgress(0);
      progressTimerRef.current = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 85) {
            if (progressTimerRef.current) clearInterval(progressTimerRef.current);
            return 85;
          }
          return prev + Math.random() * 8 + 3;
        });
      }, 300);
    } else {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
      if (uploadProgress > 0) {
        setUploadProgress(100);
        setTimeout(() => setUploadProgress(0), 600);
      }
    }
    return () => {
      if (progressTimerRef.current) clearInterval(progressTimerRef.current);
    };
  }, [isUploading]);

  const handleSubmit = () => {
    onSelectImages(selectedId);
    onCloseModal();
  };

  const handleImageSelect = (value: IImage) => {
    setSelectedId(value);
  };

  const handleClose = () => {
    onCloseModal();
    setSelectedId(selectedImage);
  };

  const { data: images, isFetching, isLoading } = useQuery({
    queryFn: async () => {
      if (!assetId) return { data: [], last_page: 1 };
      const { data } = await instance.get(
        `admin/photo-storage/knowledge_base/${assetId}`,
        { params: { perPage: 9, page } }
      );
      return data;
    },
    queryKey: ['kb-images', assetId, page],
    enabled: !!assetId,
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  const { mutateAsync: uploadPhotos } = useMutation(
    async (files: File[]) => {
      const formData = new FormData();
      files.forEach(file => formData.append('photos[]', file));
      const { data } = await instance.post('admin/photos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data as IImage[];
    },
    { onError: (error: unknown) => toastError(error) }
  );

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setIsUploading(true);
    try {
      const uploaded = await uploadPhotos(Array.from(files));
      if (uploaded?.length) {
        const first = uploaded[0];
        setSelectedId({ id: first.id, full_photo_url: first.full_photo_url, path: '' });
        setUploadedPhotos(prev => [...uploaded.map(p => ({ ...p, path: '' })), ...prev]);
        toastSuccess(t('photo_uploaded'));
      }
    } finally {
      setIsUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const storageImages: IImage[] = (images?.data || []).map(
    (photo: { id: number; photo_id: number; url: string }) => ({
      id: photo.photo_id,
      full_photo_url: photo.url,
      path: '',
    })
  );

  const uploadedIds = new Set(uploadedPhotos.map(p => p.id));
  const mappedImages = [...uploadedPhotos, ...storageImages.filter(p => !uploadedIds.has(p.id))];

  useEffect(() => {
    setSelectedId(selectedImage);
  }, [selectedImage]);

  return (
    <div className={styles.photoContainer}>
      <div className={styles.headingWrapper}>
        <p className={styles.title}>{t('choose_photo')}</p>
        <div className={styles.closeBtnWrapper}>
          {selectedId?.full_photo_url && (
            <Button
              text={t('discard')}
              className={styles.resetBtn}
              onClick={() => setSelectedId({ id: 0, full_photo_url: '', path: '' })}
            />
          )}
          <Button
            text={t('close')}
            className={styles.closeBtn}
            onClick={handleClose}
          />
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className={styles.hiddenInput}
      />

      <div className={styles.photoPickerWrapper}>
        <PhotoPicker
          photos={mappedImages}
          selectedImages={selectedId}
          onImageClick={handleImageSelect}
          isFetching={isFetching || isLoading}
          onUploadClick={() => inputRef.current?.click()}
          isUploading={isUploading}
          uploadProgress={Math.min(Math.round(uploadProgress), 100)}
        />
      </div>

      <div className={styles.paginationContainer}>
        <Pagination
          pageCount={images?.last_page || 1}
          onPageChange={(selectedPage: { selected: number }) => {
            setPage(selectedPage.selected + 1);
          }}
        />
      </div>

      <Button
        text={t('confirm')}
        className={styles.submitBtn}
        type="button"
        onClick={handleSubmit}
        disabled={!selectedId?.full_photo_url}
      />
    </div>
  );
};

export default ImageArtickeContainer;
